// Intake Part 1 step 2 "HM preferences & logistics" as data (lane B13, contract C4; DC-231: F-156, F-157). Pure: the
// form (LogisticsFields.tsx), the record-after-the-fact schema (ui/recordOperationSchema.ts) and the node suites read
// the same constants, so the half-hour starts, the window rule and the stored shape are written once on the client.

/** One availability window the Hiring Manager offers: a working day after today and a half-hour start. */
export interface AvailabilityWindow {
  date: string; // YYYY-MM-DD, the requisition's time zone
  start: string; // HH:MM on :00 or :30, from 08:00
}

/** `hm_availability` as stored (C4): the window length first, then the windows; `tz` is the server's. */
export interface HmAvailability {
  duration_minutes: 30 | 60;
  windows: AvailabilityWindow[];
  tz?: string | null;
}

export interface TravelValue {
  required: boolean;
  percent: number | null;
}

/** The window lengths, the first the default (DC-231: "30 minutes selected; chosen first"). */
export const WINDOW_LENGTHS = [30, 60] as const;
/** At most ten windows (C4 `windows: list[Window] (0–10)`). */
export const MAX_WINDOWS = 10;
/** The last minute a window may end at: 18:00. */
const DAY_START = 8 * 60;
const DAY_END = 18 * 60;
/** The logistics note and the internal-candidates text (C4: ≤ 4000). */
export const LOGISTICS_TEXT_MAX = 4000;

export const CONTACT_METHODS = ["teams", "email", "both"] as const;
export const RELOCATION_VALUES = ["yes", "no", "partial"] as const;
export const WFH_VALUES = ["remote", "office_1", "office_2", "office_3", "office_4", "office_5"] as const;
/** Every half-hour start the server takes (C4: "HH:MM" 08:00–17:30 on :00/:30). */
export const HALF_HOURS: readonly string[] = Array.from({ length: (DAY_END - DAY_START) / 30 }, (_, i) => clock(DAY_START + i * 30));
/** The keys the server's PreferencesLogisticsValues accepts (extra="forbid"). */
export const LOGISTICS_KEYS = [
  "preferred_contact_method",
  "hm_availability",
  "relocation",
  "wfh_policy",
  "travel",
  "internal_candidates_policy",
  "additional_logistics_notes",
] as const;

function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

export function minutesOf(start: string): number {
  const [h, m] = start.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** The starts a window of `duration` minutes may take: 08:00 to the last one ending by 18:00 (17:30 / 17:00). */
export function startsFor(duration: number): string[] {
  return HALF_HOURS.filter((start) => minutesOf(start) + duration <= DAY_END);
}

/** Two windows on the same day overlap when their starts are closer than the window length. */
export function overlaps(a: AvailabilityWindow, b: AvailabilityWindow, duration: number): boolean {
  return a.date === b.date && Math.abs(minutesOf(a.start) - minutesOf(b.start)) < duration;
}

export function sortWindows(windows: readonly AvailabilityWindow[]): AvailabilityWindow[] {
  return [...windows].sort((a, b) => (a.date === b.date ? minutesOf(a.start) - minutesOf(b.start) : a.date < b.date ? -1 : 1));
}

/** A working day after today: later than `today` and not a Saturday or Sunday (the server also refuses a holiday). */
export function isWorkingDayAfter(date: string, today: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date <= today) return false;
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay();
  return weekday !== 0 && weekday !== 6;
}

/** The next working day after `today` (the date input's earliest day). */
export function nextWorkingDay(today: string): string {
  const d = new Date(`${today}T12:00:00Z`);
  do d.setUTCDate(d.getUTCDate() + 1);
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
  return d.toISOString().slice(0, 10);
}

/** Today's date (YYYY-MM-DD) in `timeZone` at `now`. */
export function dayIn(now: number, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(now));
  } catch {
    return new Date(now).toISOString().slice(0, 10);
  }
}

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Tue 14 Oct" — the day as C4's availability line writes it. */
export function dayLabel(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  return `${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
}

/** "Tue 14 Oct 09:00–09:30" — one listed window (DC-231). */
export function windowLabel(window: AvailabilityWindow, duration: number): string {
  return `${dayLabel(window.date)} ${window.start}–${clock(minutesOf(window.start) + duration)}`;
}

/** The time zone's short name ("EDT") for the list, or the zone itself when the platform has none. */
export function zoneAbbreviation(timeZone: string | null | undefined, now: number): string | null {
  if (!timeZone) return null;
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" }).formatToParts(new Date(now)).find((p) => p.type === "timeZoneName")?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

/** The stored availability, or null for anything that is not C4's shape (an old free text never fills the picker). */
export function readAvailability(value: unknown): HmAvailability | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const duration = v["duration_minutes"] === 60 ? 60 : v["duration_minutes"] === 30 ? 30 : null;
  if (duration === null) return null;
  const windows = Array.isArray(v["windows"])
    ? (v["windows"] as unknown[]).filter((w): w is AvailabilityWindow => !!w && typeof w === "object" && typeof (w as AvailabilityWindow).date === "string" && typeof (w as AvailabilityWindow).start === "string").map((w) => ({ date: w.date, start: w.start }))
    : [];
  return { duration_minutes: duration, windows: sortWindows(windows), tz: typeof v["tz"] === "string" ? v["tz"] : null };
}

export function readTravel(value: unknown): TravelValue | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (typeof v["required"] !== "boolean") return null;
  return { required: v["required"], percent: v["required"] && typeof v["percent"] === "number" ? v["percent"] : null };
}

const oneOf = <T extends string>(values: readonly T[], value: unknown): T | null => (values.includes(value as T) ? (value as T) : null);
const text = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value : null);

/** The section as `intake.save_part1` takes it (C4, extra="forbid"): only its keys, each in its stored shape; a value
 * that is not (an old free text the migration had not reached) is left out rather than refused. The server's `tz` is
 * never sent back: it sets it. */
export function logisticsPayload(values: Record<string, unknown>): Record<string, unknown> {
  const availability = readAvailability(values["hm_availability"]);
  const travel = readTravel(values["travel"]);
  const out: Record<string, unknown> = {
    preferred_contact_method: oneOf(CONTACT_METHODS, values["preferred_contact_method"]),
    relocation: oneOf(RELOCATION_VALUES, values["relocation"]),
    wfh_policy: oneOf(WFH_VALUES, values["wfh_policy"]),
    travel: travel ? { required: travel.required, percent: travel.required ? travel.percent : null } : null,
    internal_candidates_policy: text(values["internal_candidates_policy"]),
    additional_logistics_notes: text(values["additional_logistics_notes"]),
  };
  if (availability) out["hm_availability"] = { duration_minutes: availability.duration_minutes, windows: availability.windows.map((w) => ({ date: w.date, start: w.start })) };
  else if (values["hm_availability"] === null) out["hm_availability"] = null;
  return out;
}
